import React, { useEffect, useState, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import DraggableModal from '../DraggableModal';
import Chat from './components/chat';
import SlidingTips from '@/components/SlidingTips';
import styles from './index.module.scss';
import SkeletonLoader from './components/SkeletonLoader';
import { CnBox, CnDialog } from '@sto/cn-ui';
import History from './components/history';
import Subscription from './components/subscription';
import { useChatStore } from '@/store';
// import TestDemo from './test';
import { parseUrlParams } from '@/utils/tools';
import SubscriptionRecord from './components/subscription/record';
import { correctIframeCoordinates, addIframeEventListeners, removeIframeEventListeners } from '@/utils/iframeUtils';
import { recommendPolling } from '@/services/fetchs';
import { zcRobots } from '@/AIChart/components/RichInput/fastQuestion';

const CHAT_BOT_POS_X = 'CHAT_BOT_POS_X';
const CHAT_BOT_POS_Y = 'CHAT_BOT_POS_Y';

// 定义 AIChart 组件的 props 类型
interface AIChartProps {
  robotInfo: any;
  robotList?: any[];
  isTestChat?: boolean;
  TriggerComponent?: React.ComponentType<any>;
  hideTriggerButton?: boolean;
  /** 自定义流式输出接口URL，支持完整URL或基础URL（会自动拼接/chat/ask/stream） */
  streamUrl?: string;
}

const AIChart = forwardRef<any, AIChartProps>(({ 
  robotInfo: defaultRobot, 
  robotList: defaultrobotList, 
  isTestChat, 
  TriggerComponent, 
  hideTriggerButton,
  streamUrl
}, ref) => {
  const { fixedStyle, botVisible, setBotVisible } = useChatStore();
  const [robotInfo, setRobotInfo] = useState(defaultRobot);
  const [recommendPollingData, setRecommendPollingData] = useState<any[]>([]);
  const previousPathnameRef = useRef<string>(''); // 记录上一个页面路径

  const onClose = () => setBotVisible(false);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    setBotVisible: (visible: boolean) => {
      setBotVisible(visible);
      return visible; // Return the new state
    },
    getBotVisible: () => botVisible, // Expose current visibility status
  }));

  // 是否展示机器人入口
  const [showRobot] = useState(false);
  const [tempMessage] = useState<any>();
  const chatBotRef = useRef<any>(null);
  const { activeContentType, setActiveContentType } = useChatStore();
  // 将选中机器人排到第一个
  const robotList = useMemo(() => {
    const findRobot = defaultrobotList?.find((e) => e.robotCode === defaultRobot.robotCode);
    if (!findRobot) return defaultrobotList;
    return [findRobot].concat(defaultrobotList?.filter((e) => e.robotCode !== defaultRobot.robotCode) || [])
  }, [defaultrobotList, defaultRobot]);

  /** ***********************************
   * 拖拽部分
   ************************************ */

  const positionRef = useRef({
    hasMoved: false, // exclude click event
    x: 0, // right
    y: 400, // bottom
    startX: 0,
    startY: 400,
    endX: 0,
    endY: 0,
  });
  const chatBotDraggerRef = useRef(null);

  const [botDraggerPos, setBotDraggerPos] = useState({
    x: 0,
    y: 400,
  });

  const setSwitchPosition = useCallback((switchX, switchY) => {
    [switchX, switchY] = _getSwitchButtonSafeAreaXY(switchX, switchY);
    positionRef.current = {
      ...positionRef.current,
      x: switchX,
      y: switchY,
    };
    setBotDraggerPos({
      x: switchX,
      y: switchY,
    });

    localStorage.setItem(CHAT_BOT_POS_X, `${switchX}`);
    localStorage.setItem(CHAT_BOT_POS_Y, `${switchY}`);
  }, []);

  useEffect(() => {
    if (chatBotDraggerRef.current && showRobot) {
      // 初始化时从localStorage中恢复数据
      const storedX = Number(localStorage.getItem(CHAT_BOT_POS_X));
      const storedY = Number(localStorage.getItem(CHAT_BOT_POS_Y));
      setSwitchPosition(storedX, storedY);
    }
  }, [setSwitchPosition, showRobot]);

  // 获取url参数，弹出具体的推送任务详情
  useEffect(() => {
    const { subscriptionId, scheduledTaskId } = parseUrlParams();
    if (subscriptionId && scheduledTaskId) {
      CnDialog.show({
        title: '订阅推送记录',
        content: <div style={{ padding: 12 }}>
          <SubscriptionRecord id={subscriptionId} scheduledTaskId={scheduledTaskId} />
        </div>,
        width: 660,
        footer: false,
      });
    };
  }, []);

  // 根据页面路径（菜单路径）推荐消息播报内容
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (zcRobots.includes(robotInfo?.robotCode)) {
      // 仅政策机器人需要推荐消息播报
      const getRecommendPollingData = () => {
        const currentPathname = window.location.pathname;
        if (currentPathname === previousPathnameRef.current) {
          return;
        }
        previousPathnameRef.current = currentPathname;
        recommendPolling({ pageUrl: currentPathname }).then(res => {
          const { success, data } = res;
          if (success && data && data.length > 0) {
            // 推荐消息播报内容
            setRecommendPollingData(data);
          } else {
            setRecommendPollingData([]);
          }
        }).catch(error => {
          console.error('Error fetching recommend polling data:', error);
          setRecommendPollingData([]);
        });
      };
      getRecommendPollingData();
      // 轮询获取播报信息
      timer = setInterval(getRecommendPollingData, 200);
    }
    return () => {
      timer && clearInterval(timer);
    };
  }, [robotInfo]);

  /**
   * Get a safe [x, y] position for switch button
   */
  const _getSwitchButtonSafeAreaXY = (x, y) => {
    const docWidth = Math.max(
      document.documentElement.offsetWidth,
      window.innerWidth,
    );
    const docHeight = Math.max(
      document.documentElement.offsetHeight,
      window.innerHeight,
    );
    // 检查边缘
    if (x + chatBotDraggerRef?.current?.offsetWidth > docWidth) {
      x = docWidth - chatBotDraggerRef?.current?.offsetWidth;
    }
    if (y + chatBotDraggerRef?.current?.offsetHeight > docHeight) {
      y = docHeight - chatBotDraggerRef?.current?.offsetHeight;
    }
    if (x < 20) {
      x = 20;
    }
    if (y < 0) {
      y = 0;
    }
    return [x, y];
  };

  /** ***********************************
   * DOM Events for Mouse (PC)
   ************************************ */


  const onMouseMove = (e) => {
    const { clientX, clientY } = correctIframeCoordinates(e);

    const offsetX = positionRef.current.startX - clientX;
    const offsetY = positionRef.current.startY - clientY;

    let x = Math.floor(positionRef.current.x + offsetX);
    let y = Math.floor(positionRef.current.y + offsetY);
    [x, y] = _getSwitchButtonSafeAreaXY(x, y);
    setBotDraggerPos({
      x,
      y,
    });
    positionRef.current = {
      ...positionRef.current,
      endX: x,
      endY: y,
      hasMoved: true,
    };
    e.preventDefault();
  };

  const onMouseUp = () => {
    if (positionRef.current.hasMoved) {
      setSwitchPosition(positionRef.current.endX, positionRef.current.endY);
    }

    // 移除 mousemove 和 mouseup 监听器
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);

    // 从所有iframe移除事件监听器
    removeIframeEventListeners('mousemove', onMouseMove);
    removeIframeEventListeners('mouseup', onMouseUp);
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { clientX, clientY } = correctIframeCoordinates(e);

    positionRef.current = {
      ...positionRef.current,
      startX: clientX,
      startY: clientY,
      hasMoved: false,
    };
    // 添加 mousemove 和 mouseup 监听器
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    // 为所有iframe添加事件监听器
    addIframeEventListeners('mousemove', onMouseMove);
    addIframeEventListeners('mouseup', onMouseUp);
  };

  const handleBotClick = () => {
    if (positionRef.current.hasMoved) {
      return;
    }
    setBotVisible(!botVisible);
  };

  const renderTriggerComponent = useMemo(() => {
    try {
      // 机器人图标, 取后端返回的
      const defaultAvatar = 'https://imgcdn.sto.cn/A7F4FCA3-E610-45-200x200-%E7%94%B3%E6%87%82%E6%87%82%E5%A4%96%E9%83%A8%E5%85%A5%E5%8F%A3.gif'; 
      const avatar = robotInfo?.avatar || defaultAvatar;
      if (TriggerComponent && typeof TriggerComponent === 'function') {
        return <TriggerComponent onClick={handleBotClick} />;
      } else if (TriggerComponent && React.isValidElement(TriggerComponent)) {
        return (
          <div
            ref={chatBotDraggerRef}
            onMouseDown={onMouseDown}
          >
            <img
              className={styles.img}
              onClick={handleBotClick}
              src={avatar}
              alt=""
            />
            {TriggerComponent}
          </div>
        );
      } else if (isTestChat) {
        return (
          <div
            className={styles.testIcon}
            ref={chatBotDraggerRef}
            onMouseDown={onMouseDown}
            onClick={handleBotClick}
          >
            测试
          </div>
        );
      } else {
        return (
          <img
            className={styles.img}
            ref={chatBotDraggerRef}
            onMouseDown={onMouseDown}
            onClick={handleBotClick}
            src={avatar}
            alt=""
          />
        )
      }
    } catch (error) {
      console.error('Error rendering trigger component:', error);
      return null;
    }
  }, [TriggerComponent, isTestChat, onMouseDown, robotInfo]);

  /** 历史会话列表区域的宽度 */
  const historyWidth = useMemo(() => {
    const width = fixedStyle.width * 0.158;
    if (width < 148) {
      return 148;
    }
    return width;
  }, [fixedStyle.width]);

  const tips = useMemo(() => {
    if (recommendPollingData && recommendPollingData.length > 0) {
      return recommendPollingData;
    }
    return [
      'Hi~我是AI助手小申，有任何问题都可以问我哟，快来体验吧~',
      '遇到不懂的问题了？快让AI助手小申帮你解答吧~',
    ];
  }, [recommendPollingData]);

	return (
    <>
      {!hideTriggerButton && (
        <div
          className={styles.chatBotBtn}
          style={{
            right: `0px`,
            bottom: `${botDraggerPos.y}px`,
          }}
        >
          {renderTriggerComponent}
          <SlidingTips
            tips={tips}
            tempMessage={tempMessage}
            onClick={(v, extend) => {
              // 确保切换到聊天模式
              if (activeContentType !== 'chat') {
                setActiveContentType('chat');
              }
              setBotVisible(true);
              
              // 使用重试机制确保 Chat 组件已经挂载
              const trySubmit = (retries = 20, delay = 100) => {
                
                if (chatBotRef.current?.onSubmit) {
                  v && chatBotRef.current.onSubmit({ content: v, extend });
                } else if (retries > 0) {
                  setTimeout(() => trySubmit(retries - 1, delay), delay);
                } else {
                  console.warn('Chat 组件在重试后仍未挂载，可能的原因：', {
                    botVisible,
                    activeContentType,
                    chatBotRef: chatBotRef.current,
                  });
                }
              };
              
              // 初始延迟，等待组件渲染
              setTimeout(() => {
                trySubmit();
              }, 300);
            }}
          />
        </div>
      )}

      <DraggableModal
        visible={botVisible}
        onClose={onClose}
        robotInfo={robotInfo}
        robotList={robotList}
        onChangeRobot={setRobotInfo}
      >
        {robotInfo ? (
          <CnBox 
            direction="row" 
            className={styles.chatContainer} 
            style={{ gridTemplateColumns: `${historyWidth}px 1fr` }}
          >
            <div className={styles.historySticky}>
              <History robotInfo={robotInfo} />
            </div>
            {/* 内容区域：聊天区域或订阅区域 */}
            {activeContentType === 'chat' ? 
              <Chat
                ref={chatBotRef}
                robotInfo={robotInfo}
                isTestChat={isTestChat}
                streamUrl={streamUrl}
              /> :
              <Subscription />
            }
          </CnBox>
        ) : (
          <SkeletonLoader />
        )}
      </DraggableModal>
      {/* <TestDemo /> */}
    </>
  );
});

export default AIChart;
